import { Observable, Subscription } from 'rxjs';
import { ItineraryService } from '../../services/itinerary.service';
import { ContractError, IAlertMessage } from 'src/app/core/models/models';
import { map } from 'rxjs/operators';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { InfantControlService } from 'src/app/search-result/services/infant-control.service';
import { IAdultsInCharge } from 'src/app/core/models/infant-reassignment-control.model';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { ReportLogService } from 'src/app/shared/services/report-log.service';
import {
  ILogInLogState,
  Passenger,
  PassengerQuote,
  PnrInfo
} from 'src/app/core/models/itinerary.model';
import {
  Component,
  HostListener,
  KeyValueDiffer,
  KeyValueDiffers,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  ActionTypeText,
  ALLOWED_CHANGES_PLACEHOLDER_MESSAGES,
  CouponEmdType,
  GDSs,
  OptionType,
  PassengerType
} from 'src/app/core/enums/enums';

const infantAlertMessage: IAlertMessage = {
  message: 'layout.alert-messages.infant-reassignment',
  isAccepted: false,
  translate: true
};

@Component({
  selector: 'app-itinerary-info',
  templateUrl: './itinerary-info.component.html',
  styleUrls: ['./itinerary-info.component.scss']
})
export class ItineraryInfoComponent implements OnInit, OnDestroy {
  pnrInfo$: Observable<PnrInfo>;
  error$: Observable<ContractError>;
  selectedMode$: Observable<string>;
  somePassengerHasTicket$: Observable<boolean>;
  getSelectedPassengersHasInvalidCoupons$: Observable<boolean>;

  subscriptions = new Subscription();

  pnrInfo: PnrInfo;
  optionType = OptionType;
  actionType: string;
  isException: boolean;
  infantConditionsMet: boolean = false;
  isAllowedInfantReasignation: boolean = false;
  differ: KeyValueDiffer<string, any>;
  error: boolean;
  errorChangesByException: boolean;
  messageError: string;
  typeElection: string;
  manualAdultsIncharge: IAdultsInCharge[] = [];
  adultsFullNamesWithInfants: string[] = [];
  isAirline: boolean;
  showVoidTransactionModal: boolean;
  voidTransactionEnabled: boolean = false;
  removeAlertMessageValidation: boolean = false;
  pnrIsEncrypted = false;
  isVoluntaryChangeDisabled = false;
  voluntaryChangeAllowedMessage = ALLOWED_CHANGES_PLACEHOLDER_MESSAGES.voluntaryChange;
  showOAGFltModal = false;
  logInLogState: ILogInLogState;
  showLogInLogModal = false;
  logInLogInitialComprobation = true;

  constructor(
    private service: ItineraryService,
    private layoutService: LayoutService,
    private settingsService: SettingsService,
    private differs: KeyValueDiffers,
    private infantControlService: InfantControlService,
    private translate: TranslateService,
    private reportLog: ReportLogService
  ) {
    this.differ = this.differs.find({}).create();
    this.infantControlService.getAdultsIncharge().subscribe((res) => {
      this.manualAdultsIncharge = res;
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    this.reportLog.FinishByUser(ActionTypeText.NA);
  }

  ngOnInit() {
    this.getSelectedPassengersHasInvalidCoupons$ =
      this.service.getSelectedPassengersHasInvalidCoupons();
    this.pnrInfo$ = this.service.getPnrInfo().pipe(
      map((info) => {
        this.verifyInfantBeforeChange(info);
        this.ValidateIsAirline(info);
        this.extractAdultsFullNamesWithInfants(info);
        this.pnrInfo = info;
        this.pnrIsEncrypted = info?.isEncrypted ? true : false;
        this.isVoluntaryChangeDisabled = !info?.isVoluntaryChangeAllowed;
        this.voluntaryChangeAllowedMessage = info?.voluntaryChangeAllowedMessage;
        return info;
      })
    );

    this.error$ = this.service.getError();
    this.selectedMode$ = this.service.getActionActive();
    this.somePassengerHasTicket$ = this.service.getSomePassengerHasTicket();
    this.callComponentSubscriptions();
  }

  callComponentSubscriptions() {
    this.subscriptions.add(
      this.settingsService.getClientSettings().subscribe((settings) => {
        if (settings.infantAssociationEnabled) {
          this.isAllowedInfantReasignation = settings.infantAssociationEnabled;
        }
        if (settings.voidTransactionEnabled) {
          this.voidTransactionEnabled = settings.voidTransactionEnabled;
        }
      })
    );

    this.subscriptions.add(
      this.service.getLogInLogState().subscribe((state) => {
        if (!state?.logInLogRegistered && !state?.isError && this.logInLogInitialComprobation) {
          this.service.dispatchPostLogInLog();
          this.logInLogInitialComprobation = false;
        }

        if (state?.isError) {
          this.showLogInLogModal = true;
        }
      })
    );
  }

  ValidateIsAirline(info: PnrInfo) {
    this.settingsService.getClientIsAirline().subscribe((resultParameterAirline) => {
      this.isAirline = resultParameterAirline;
      if (resultParameterAirline) {
        this.ValidateNoShow(info);
      }
    });
  }

  ValidateNoShow(infoPnr: PnrInfo) {
    this.error = infoPnr?.isNoShow;
  }

  ValidateChangeForException(infoPnr: PnrInfo) {
    let issueDateHours = new Date(infoPnr.passengers[0]?.ticket?.issuedDate);
    let issueDateRetrieve = new Date(infoPnr.passengers[0]?.ticket?.issuedDate);
    const today = new Date(infoPnr.passengers[0].ticket.actualDateTimeZone);
    issueDateHours.setDate(issueDateHours.getDate() + 1);
    let todayWithOut = new Date(today.getFullYear(), today.getMonth(), today.getDay());
    let issueDateRetrieveWithOut = new Date(
      issueDateRetrieve.getFullYear(),
      issueDateRetrieve.getMonth(),
      issueDateRetrieve.getDay()
    );
    if (
      today <= issueDateHours &&
      todayWithOut.getTime() === issueDateRetrieveWithOut.getTime() &&
      infoPnr.isIssueException
    ) {
      this.messageError = 'exception-mode.warning-ChangesByException';
      this.errorChangesByException = true;
    }
  }

  extractAdultsFullNamesWithInfants(pnrInfo: PnrInfo) {
    pnrInfo?.passengers.forEach((passenger) => {
      if (passenger.type === PassengerType.Infant) {
        this.adultsFullNamesWithInfants.push(passenger.adultInCharge);
      }
    });
  }

  ngDoCheck() {
    const change = this.differ.diff(this);

    if (change) {
      this.selectedMode$.subscribe((mode) => {
        this.typeElection = mode;
      });

      this.callPnrInfoSubscription();

      this.callGetSelectedPassengersQuoteSubscription();

      this.callGetSomePassengerHasAncillaryTypeSubscription();
    }
  }

  callPnrInfoSubscription() {
    this.subscriptions.add(
      this.pnrInfo$.subscribe((pnr) => {
        if (this.typeElection === OptionType.VoluntaryChange && this.isAirline) {
          this.ValidateChangeForException(pnr);
        } else {
          this.errorChangesByException = false;
        }
      })
    );
  }

  callGetSelectedPassengersQuoteSubscription() {
    this.subscriptions.add(
      this.service.getSelectedPassengersQuote().subscribe((selectedPassengers) => {
        if (this.checkManualInfantReassignment()) {
          this.removeAlertMessageValidation && this.removeInfantAlert();
        } else {
          if (selectedPassengers && selectedPassengers.length > 0) {
            let hasAdultWithInfant = false;

            selectedPassengers.forEach((passenger) => {
              if (
                this.adultsFullNamesWithInfants.includes(
                  `${passenger.firstName} ${passenger.lastName}`
                )
              ) {
                hasAdultWithInfant = true;
                let infantPassengerSelected = selectedPassengers.findIndex(
                  (x) => x.passengerType === PassengerType.Infant
                );
                if (
                  this.infantConditionsMet &&
                  this.isAllowedInfantReasignation &&
                  infantPassengerSelected < 0
                ) {
                  this.layoutService.saveNewAlertMessage(infantAlertMessage);
                  this.removeAlertMessageValidation = true;
                } else {
                  this.removeAlertMessageValidation && this.removeInfantAlert();
                }
              }
            });

            if (!hasAdultWithInfant) {
              this.removeAlertMessageValidation && this.removeInfantAlert();
            }
          } else {
            this.removeAlertMessageValidation && this.removeInfantAlert();
          }
        }
      })
    );
  }

  callGetSomePassengerHasAncillaryTypeSubscription() {
    this.subscriptions.add(
      this.service.getSomePassengerHasAncillaryTypeS().subscribe((passengers) => {
        if (passengers?.length > 0 && this.typeElection === OptionType.VoluntaryChange) {
          const message = this.getPassengerMessage(passengers);

          const ancillariesAlertMessage: IAlertMessage = {
            message: `${message} ${this.translate.instant(
              'layout.alert-messages.ancillaries-reassignment-confirmation'
            )}`,
            isAccepted: false,
            translate: false
          };

          this.layoutService.saveNewAlertMessage(ancillariesAlertMessage);
        }
      })
    );
  }

  getPassengerMessage(passengers: Passenger[]): string {
    return passengers
      .map((passenger) => {
        const ancillaries = passenger.specialRequests
          .filter(
            (specialRequest) =>
              specialRequest.coupons.length > 0 &&
              specialRequest.coupons.some((coupon) => coupon.emdtype === CouponEmdType.S)
          )
          .map((ancillary) => ` ${ancillary.code} ${ancillary.name}`)
          .join(',');

        return this.translate.instant('layout.alert-messages.ancillaries-reassignment', {
          name: `${passenger.firstName} ${passenger.lastName}`,
          ancillary: ancillaries
        });
      })
      .join('. ');
  }

  removeInfantAlert() {
    this.layoutService.removeAlertMessage(infantAlertMessage);
    this.removeAlertMessageValidation = false;
  }

  selectFlights(selectedSegments: number[]) {
    this.service.dispatchSelectedSegment(selectedSegments);
  }

  selectPassengers(selectedPassengersQuote: PassengerQuote[]) {
    this.service.dispatchSelectedPassengers(selectedPassengersQuote);
  }

  goToInvoluntary() {
    this.service.navigateTo('/involuntary-change');
  }

  gdsIsAllowedForInfantAlert(info: PnrInfo): boolean {
    return info.gds === GDSs[0].code || info.gds === GDSs[3].code;
  }

  checkManualInfantReassignment(): boolean {
    return this.manualAdultsIncharge.length > 0;
  }

  verifyInfantBeforeChange(info: PnrInfo) {
    if (info && info.gds && this.gdsIsAllowedForInfantAlert(info)) {
      if (info.passengers) {
        let adultCount = 0;
        let infantCount = 0;

        info.passengers.forEach((passenger) => {
          switch (passenger.type) {
            case PassengerType.Adult:
              adultCount++;
              break;

            case PassengerType.Infant:
              infantCount++;
              break;

            default:
              break;
          }
        });

        if (infantCount > 0 && adultCount > 1 && !this.checkManualInfantReassignment()) {
          this.infantConditionsMet = true;
        }
      }
    }
  }

  voidTransactionModal(resetNeeded: boolean) {
    this.showVoidTransactionModal = !this.showVoidTransactionModal;
    if (resetNeeded) {
      window.location.reload();
    }
  }

  switchOAGFltModal() {
    this.showOAGFltModal = !this.showOAGFltModal;
  }

  switchLogInLogModal() {
    this.showLogInLogModal = !this.showLogInLogModal;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
